import React from "react";
import { Close } from "../icons";
import { useModalDialog } from "./ui";

/**
 * Catches a render error below it and shows `fallback` in its place (DSPRT-291).
 * Without one, a single exception anywhere unmounted the whole app and left
 * the ground colour and nothing else.
 *
 * A class because that is the only kind of component React lets catch one.
 */
export class ErrorBoundary extends React.Component<
  { fallback: (error: Error) => React.ReactNode; children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // The one place it is reported: there is no error service to send it to.
    console.error("StatSummit render error", error, info.componentStack);
  }

  render() {
    return this.state.error ? this.props.fallback(this.state.error) : this.props.children;
  }
}

/**
 * The whole page's fallback. Styled inline and by the boot theme rather than
 * the stylesheet's tokens: those hang off `.app`, which is the thing that
 * just failed to render.
 */
export function PageCrash() {
  const light = document.documentElement.getAttribute("data-boot-theme") === "light";
  const ink = light ? "#11151C" : "#F7F9FC";
  const muted = light ? "#4A5261" : "#A4ABB8";
  const edge = light ? "#D5DAE2" : "#272C36";
  const btn: React.CSSProperties = {
    display: "inline-flex", alignItems: "center", minHeight: 44, padding: "0 18px", borderRadius: 999,
    border: `1px solid ${edge}`, color: ink, font: "600 14px/1 Archivo, system-ui, sans-serif", textDecoration: "none",
    background: "none", cursor: "pointer",
  };
  return (
    <main role="alert" style={{ minHeight: "100vh", display: "grid", placeContent: "center", padding: 24, textAlign: "center", color: ink, fontFamily: "Archivo, system-ui, sans-serif" }}>
      <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800 }}>Something went wrong</h1>
      <p style={{ margin: "10px 0 22px", color: muted, fontSize: 15, maxWidth: "44ch" }}>
        This page hit an error and couldn&apos;t be shown. Reloading usually fixes it.
      </p>
      <p style={{ margin: 0, display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
        <button type="button" style={{ ...btn, background: ink, color: light ? "#FFFFFF" : "#0A0C10", borderColor: ink }} onClick={() => location.reload()}>Reload</button>
        <a style={btn} href="/">Home</a>
      </p>
    </main>
  );
}

/**
 * A profile or box score that failed to render: a small dialog in its place,
 * so the page underneath stays up and Close goes back to it.
 */
export function ModalCrash({ onClose }: { onClose: () => void }) {
  const ref = useModalDialog<HTMLDialogElement>();
  return (
    <dialog
      className="modal"
      ref={ref}
      aria-label="Couldn't be shown"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal__panel">
        <header className="modal__head">
          <h2 className="modal__title">Couldn&apos;t be shown</h2>
          <button className="modal__close" type="button" onClick={onClose} aria-label="Close">
            <span className="modal__closekey">Esc</span><Close />
          </button>
        </header>
        <div className="modal__body">
          <div className="notfound">
            <p className="notfound__lead">This one hit an error and couldn&apos;t be shown.</p>
            <p className="notfound__hint">The page behind it is fine. Reloading usually fixes it.</p>
            <nav className="notfound__links" aria-label="What to do">
              <button type="button" className="notfound__link" onClick={() => location.reload()}>Reload</button>
              <button type="button" className="notfound__link" onClick={onClose}>Close</button>
            </nav>
          </div>
        </div>
      </div>
    </dialog>
  );
}

/** A modal under its own boundary: a crash in it leaves the page behind standing. */
export function ModalBoundary({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return <ErrorBoundary fallback={() => <ModalCrash onClose={onClose} />}>{children}</ErrorBoundary>;
}
